/**
 * Settings Page
 * Manage application settings and configurations
 */

import React, { useState, useEffect } from "react";
import { usePageTitle } from "../contexts/PageTitleContext";
import { settingsService } from "../services/api/settings.service";
import { serviceChargeService } from "../services/api/serviceCharge.service";
import { requiredDocumentService } from "../services/api/requiredDocument.service";
import type { UpdateSettingDto } from "../types/settings.types";
import type {
  ServiceCharge,
  CreateServiceChargeDto,
  UpdateServiceChargeDto,
} from "../types/serviceCharge.types";
import type {
  RequiredDocument,
  CreateRequiredDocumentDto,
  UpdateRequiredDocumentDto,
} from "../types/requiredDocument.types";
import BusyOverlay from "../components/common/BusyOverlay";
import { apiRequest } from "../api/apiClient";
import Topbar from "../components/Topbar";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import {
  showSuccessToast,
  showErrorToast,
  showDeleteConfirm,
} from "../utils/toast";
import { GENERIC_ERROR_MESSAGE } from "../utils/constants";
import "../styles/pages/Settings.css";

// Accordion section types
type AccordionSection =
  | "general"
  | "email"
  | "notifications"
  | "security"
  | "serviceCharges"
  | "requiredDocuments";

interface ModuleOption {
  id: number;
  name: string;
  isActive?: boolean;
}

interface DocumentTypeOption {
  id: number;
  name: string;
  isActive?: boolean;
}

export const Settings: React.FC = () => {
  const { setPageTitle } = usePageTitle();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Use Set for expanded sections
  const [expandedSections, setExpandedSections] = useState<
    Set<AccordionSection>
  >(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [currencies, setCurrencies] = useState<any[]>([]);
  const [serviceCategories, setServiceCategories] = useState<any[]>([]);
  const [modules, setModules] = useState<ModuleOption[]>([]);
  const [documentTypes, setDocumentTypes] = useState<DocumentTypeOption[]>([]);

  // Service Charges State
  const [serviceCharges, setServiceCharges] = useState<ServiceCharge[]>([]);
  const [showServiceChargeModal, setShowServiceChargeModal] = useState(false);
  const [editingServiceCharge, setEditingServiceCharge] =
    useState<ServiceCharge | null>(null);
  const [serviceChargeFormData, setServiceChargeFormData] =
    useState<CreateServiceChargeDto>({
      serviceCategoryId: 0,
      lraPayment: 0,
      operationalCost: 0,
      currencyId: 0,
    });
  const [serviceChargeLoading, setServiceChargeLoading] = useState(false);
  const [serviceChargeError, setServiceChargeError] = useState("");

  // Required Documents State
  const [requiredDocuments, setRequiredDocuments] = useState<
    RequiredDocument[]
  >([]);
  const [showRequiredDocumentModal, setShowRequiredDocumentModal] =
    useState(false);
  const [editingRequiredDocument, setEditingRequiredDocument] =
    useState<RequiredDocument | null>(null);
  const [requiredDocumentFormData, setRequiredDocumentFormData] =
    useState<CreateRequiredDocumentDto>({
      moduleId: 0,
      documentTypeId: 0,
      isRequired: true,
    });
  const [requiredDocumentLoading, setRequiredDocumentLoading] = useState(false);
  const [requiredDocumentError, setRequiredDocumentError] = useState("");

  // General Settings
  const [generalSettings, setGeneralSettings] = useState({
    appName: "Traditional Marriage CMS",
    appDescription: "Traditional Marriage Document Management System",
    declaration: "",
    ministryName: "Ministry of Local Government",
    currency: "KES",
    dateFormat: "DD/MM/YYYY",
    minimumAge: "18",
  });

  // Email Settings
  const [emailSettings, setEmailSettings] = useState({
    smtpHost: "",
    smtpPort: "587",
    smtpUser: "",
    smtpPassword: "",
    fromEmail: "",
    fromName: "",
  });

  // Notification Settings
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    systemAlerts: true,
    userUpdates: true,
    workflowReminders: true,
  });

  // Notification Settings Descriptions (from database)
  const [notificationDescriptions, setNotificationDescriptions] = useState({
    emailNotifications: "Receive notifications via email",
    smsNotifications: "Receive notifications via SMS",
    systemAlerts: "Receive system alert notifications",
    userUpdates: "Receive user update notifications",
    workflowReminders: "Receive workflow reminder notifications",
  });

  // Security Settings
  const [securitySettings, setSecuritySettings] = useState({
    sessionTimeout: "30",
    passwordMinLength: "8",
    requireSpecialChar: true,
    requireNumber: true,
    requireUppercase: true,
    enableTwoFactor: false,
  });

  useEffect(() => {
    const isDesktop = window.matchMedia("(min-width: 881px)").matches;
    setSidebarOpen(isDesktop);

    const handleResize = () => {
      const isDesktopView = window.matchMedia("(min-width: 881px)").matches;
      setSidebarOpen(isDesktopView);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setPageTitle("Settings", "Configure application settings and preferences");
    fetchSettings();
    fetchCurrencies();
    fetchServiceCategories();
    fetchServiceCharges();
    fetchModules();
    fetchDocumentTypes();
    fetchRequiredDocuments();
  }, [setPageTitle]);

  const toggleSection = (section: AccordionSection) => {
    setExpandedSections((prev) => {
      // If clicking the same section that's already open, close it
      if (prev.has(section) && prev.size === 1) {
        return new Set();
      }
      // Otherwise, open only this section (single-expand behavior)
      return new Set([section]);
    });
  };

  const expandAll = () => {
    setExpandedSections(
      new Set([
        "general",
        "email",
        "notifications",
        "security",
        "serviceCharges",
        "requiredDocuments",
      ]),
    );
  };

  const fetchModules = async () => {
    try {
      const response = await apiRequest("/Modules", {
        method: "GET",
      });
      const data = await response.json();
      const moduleData = Array.isArray(data) ? data : (data as any)?.data || [];
      const nonWorkflowModules = moduleData.filter(
        (item: any) => item.workflowModule === false,
      );
      setModules(nonWorkflowModules);
    } catch (err) {
      console.error("Error fetching modules:", err);
    }
  };

  const fetchDocumentTypes = async () => {
    try {
      const response = await apiRequest("/MasterData", {
        method: "GET",
      });
      const data = await response.json();
      const masterData = Array.isArray(data) ? data : (data as any)?.data || [];

      const activeDocumentTypes = masterData.filter(
        (item: any) =>
          item.isActive !== false &&
          (item.category?.toLowerCase() === "document type" ||
            item.category?.toLowerCase() === "documenttype"),
      );

      setDocumentTypes(activeDocumentTypes);
    } catch (err) {
      console.error("Error fetching document types:", err);
    }
  };

  const collapseAll = () => {
    setExpandedSections(new Set());
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError("");
      const settings = await settingsService.getSettings();

      settings.forEach((setting) => {
        const value = setting.value;
        const key = setting.key;
        const normalizedKey =
          key && key.length > 0 ? key[0].toLowerCase() + key.slice(1) : key;

        switch (setting.category) {
          case "general":
            if (normalizedKey === "appName")
              setGeneralSettings((prev) => ({ ...prev, appName: value }));
            if (normalizedKey === "appDescription")
              setGeneralSettings((prev) => ({
                ...prev,
                appDescription: value,
              }));
            if (normalizedKey === "declaration")
              setGeneralSettings((prev) => ({ ...prev, declaration: value }));
            if (normalizedKey === "ministryName")
              setGeneralSettings((prev) => ({ ...prev, ministryName: value }));
            if (
              normalizedKey === "currency" ||
              normalizedKey === "defaultCurrency"
            )
              setGeneralSettings((prev) => ({ ...prev, currency: value }));
            if (normalizedKey === "dateFormat")
              setGeneralSettings((prev) => ({ ...prev, dateFormat: value }));

            if (normalizedKey === "minimumAge")
              setGeneralSettings((prev) => ({ ...prev, minimumAge: value }));
            break;
          case "email":
            if (normalizedKey === "smtpHost")
              setEmailSettings((prev) => ({ ...prev, smtpHost: value }));
            if (normalizedKey === "smtpPort")
              setEmailSettings((prev) => ({ ...prev, smtpPort: value }));
            if (normalizedKey === "smtpUser")
              setEmailSettings((prev) => ({ ...prev, smtpUser: value }));
            if (normalizedKey === "smtpPassword")
              setEmailSettings((prev) => ({ ...prev, smtpPassword: value }));
            if (normalizedKey === "fromEmail")
              setEmailSettings((prev) => ({ ...prev, fromEmail: value }));
            if (normalizedKey === "fromName")
              setEmailSettings((prev) => ({ ...prev, fromName: value }));
            break;
          case "notifications":
            if (normalizedKey === "emailNotifications") {
              setNotificationSettings((prev) => ({
                ...prev,
                emailNotifications: value === "true",
              }));
              if (setting.description) {
                setNotificationDescriptions((prev) => ({
                  ...prev,
                  emailNotifications:
                    setting.description || prev.emailNotifications,
                }));
              }
            }
            if (normalizedKey === "smsNotifications") {
              setNotificationSettings((prev) => ({
                ...prev,
                smsNotifications: value === "true",
              }));
              if (setting.description) {
                setNotificationDescriptions((prev) => ({
                  ...prev,
                  smsNotifications:
                    setting.description || prev.smsNotifications,
                }));
              }
            }
            if (normalizedKey === "systemAlerts") {
              setNotificationSettings((prev) => ({
                ...prev,
                systemAlerts: value === "true",
              }));
              if (setting.description) {
                setNotificationDescriptions((prev) => ({
                  ...prev,
                  systemAlerts: setting.description || prev.systemAlerts,
                }));
              }
            }
            if (normalizedKey === "userUpdates") {
              setNotificationSettings((prev) => ({
                ...prev,
                userUpdates: value === "true",
              }));
              if (setting.description) {
                setNotificationDescriptions((prev) => ({
                  ...prev,
                  userUpdates: setting.description || prev.userUpdates,
                }));
              }
            }
            if (normalizedKey === "workflowReminders") {
              setNotificationSettings((prev) => ({
                ...prev,
                workflowReminders: value === "true",
              }));
              if (setting.description) {
                setNotificationDescriptions((prev) => ({
                  ...prev,
                  workflowReminders:
                    setting.description || prev.workflowReminders,
                }));
              }
            }
            break;
          case "security":
            if (normalizedKey === "sessionTimeout")
              setSecuritySettings((prev) => ({
                ...prev,
                sessionTimeout: value,
              }));
            if (normalizedKey === "passwordMinLength")
              setSecuritySettings((prev) => ({
                ...prev,
                passwordMinLength: value,
              }));
            if (normalizedKey === "requireSpecialChar")
              setSecuritySettings((prev) => ({
                ...prev,
                requireSpecialChar: value === "true",
              }));
            if (normalizedKey === "requireNumber")
              setSecuritySettings((prev) => ({
                ...prev,
                requireNumber: value === "true",
              }));
            if (normalizedKey === "requireUppercase")
              setSecuritySettings((prev) => ({
                ...prev,
                requireUppercase: value === "true",
              }));
            if (normalizedKey === "enableTwoFactor")
              setSecuritySettings((prev) => ({
                ...prev,
                enableTwoFactor: value === "true",
              }));
            break;
        }
      });
    } catch (err: any) {
      console.error("Error fetching settings:", err);
      setError(err?.message || "Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrencies = async () => {
    try {
      const response = await apiRequest("/Currencies", {
        method: "GET",
      });
      const data = await response.json();
      const currencyData = Array.isArray(data)
        ? data
        : (data as any)?.data || [];
      const activeCurrencies = currencyData.filter(
        (item: any) => item.isActive !== false,
      );
      setCurrencies(activeCurrencies);
    } catch (err) {
      console.error("Error fetching currencies:", err);
    }
  };

  const fetchServiceCategories = async () => {
    try {
      const response = await apiRequest("/service-category", {
        method: "GET",
      });
      const data = await response.json();
      const categoryData = Array.isArray(data)
        ? data
        : (data as any)?.data || [];
      // Filter only active categories if needed
      const activeCategories = categoryData.filter(
        (item: any) => item.isActive !== false,
      );
      setServiceCategories(activeCategories);
    } catch (err) {
      console.error("Error fetching service categories:", err);
    }
  };

  const fetchServiceCharges = async () => {
    try {
      const prices = await serviceChargeService.getServiceCharges();
      setServiceCharges(prices);
    } catch (err: any) {
      console.error("Error fetching service prices:", err);
    }
  };

  const fetchRequiredDocuments = async () => {
    try {
      const data = await requiredDocumentService.getRequiredDocuments();
      setRequiredDocuments(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error("Error fetching required documents:", err);
    }
  };

  const openServiceChargeModal = (serviceCharge?: ServiceCharge) => {
    if (serviceCharge) {
      setEditingServiceCharge(serviceCharge);
      setServiceChargeFormData({
        serviceCategoryId: serviceCharge.serviceCategoryId,
        lraPayment: serviceCharge.lraPayment,
        operationalCost: serviceCharge.operationalCost,
        currencyId: serviceCharge.currencyId,
      });
    } else {
      setEditingServiceCharge(null);
      setServiceChargeFormData({
        serviceCategoryId: 0,
        lraPayment: 0,
        operationalCost: 0,
        currencyId: 0,
      });
    }
    setServiceChargeError("");
    setShowServiceChargeModal(true);
  };

  const closeServiceChargeModal = () => {
    setShowServiceChargeModal(false);
    setEditingServiceCharge(null);
    setServiceChargeFormData({
      serviceCategoryId: 0,
      lraPayment: 0,
      operationalCost: 0,
      currencyId: 0,
    });
    setServiceChargeError("");
  };

  const handleServiceChargeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setServiceChargeError("");

    if (
      !serviceChargeFormData.serviceCategoryId ||
      serviceChargeFormData.serviceCategoryId === 0
    ) {
      setServiceChargeError("Please select a service category");
      return;
    }
    if (
      !serviceChargeFormData.currencyId ||
      serviceChargeFormData.currencyId === 0
    ) {
      setServiceChargeError("Please select a currency");
      return;
    }
    if (
      !serviceChargeFormData.lraPayment ||
      serviceChargeFormData.lraPayment <= 0
    ) {
      setServiceChargeError("Please enter a valid LRA Payment amount");
      return;
    }
    if (
      !serviceChargeFormData.operationalCost ||
      serviceChargeFormData.operationalCost <= 0
    ) {
      setServiceChargeError("Please enter a valid Operational Cost");
      return;
    }

    try {
      setServiceChargeLoading(true);

      if (editingServiceCharge) {
        await serviceChargeService.updateServiceCharge(
          editingServiceCharge.id!,
          {
            id: editingServiceCharge.id,
            ...serviceChargeFormData,
          } as UpdateServiceChargeDto,
        );
        showSuccessToast("Service price updated successfully");
      } else {
        await serviceChargeService.createServiceCharge(serviceChargeFormData);
        showSuccessToast("Service price created successfully");
      }

      await fetchServiceCharges();
      closeServiceChargeModal();
    } catch (err: any) {
      setServiceChargeError(err?.message || "Failed to save service price");
    } finally {
      setServiceChargeLoading(false);
    }
  };

  const handleDeleteServiceCharge = async (serviceCharge: ServiceCharge) => {
    const confirmed = await showDeleteConfirm(
      serviceCharge.serviceCategoryName || `Service Price #${serviceCharge.id}`,
      "service price",
    );

    if (!confirmed) return;

    try {
      setServiceChargeLoading(true);
      await serviceChargeService.deleteServiceCharge(serviceCharge.id!);
      showSuccessToast("Service price deleted successfully");
      await fetchServiceCharges();
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setServiceChargeLoading(false);
    }
  };

  const openRequiredDocumentModal = (requiredDocument?: RequiredDocument) => {
    if (requiredDocument) {
      setEditingRequiredDocument(requiredDocument);
      setRequiredDocumentFormData({
        moduleId: requiredDocument.moduleId,
        documentTypeId: requiredDocument.documentTypeId,
        isRequired: requiredDocument.isRequired,
      });
    } else {
      setEditingRequiredDocument(null);
      setRequiredDocumentFormData({
        moduleId: 0,
        documentTypeId: 0,
        isRequired: true,
      });
    }
    setRequiredDocumentError("");
    setShowRequiredDocumentModal(true);
  };

  const closeRequiredDocumentModal = () => {
    setShowRequiredDocumentModal(false);
    setEditingRequiredDocument(null);
    setRequiredDocumentFormData({
      moduleId: 0,
      documentTypeId: 0,
      isRequired: true,
    });
    setRequiredDocumentError("");
  };

  const handleRequiredDocumentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setRequiredDocumentError("");

    if (
      !requiredDocumentFormData.moduleId ||
      requiredDocumentFormData.moduleId === 0
    ) {
      setRequiredDocumentError("Please select a module");
      return;
    }

    if (
      !requiredDocumentFormData.documentTypeId ||
      requiredDocumentFormData.documentTypeId === 0
    ) {
      setRequiredDocumentError("Please select a document type");
      return;
    }

    try {
      setRequiredDocumentLoading(true);

      if (editingRequiredDocument) {
        await requiredDocumentService.updateRequiredDocument(
          editingRequiredDocument.id,
          {
            id: editingRequiredDocument.id,
            ...requiredDocumentFormData,
          } as UpdateRequiredDocumentDto,
        );
        showSuccessToast("Required document updated successfully");
      } else {
        await requiredDocumentService.createRequiredDocument(
          requiredDocumentFormData,
        );
        showSuccessToast("Required document created successfully");
      }

      await fetchRequiredDocuments();
      closeRequiredDocumentModal();
    } catch (err: any) {
      setRequiredDocumentError(
        err?.message || "Failed to save required document",
      );
    } finally {
      setRequiredDocumentLoading(false);
    }
  };

  const handleDeleteRequiredDocument = async (
    requiredDocument: RequiredDocument,
  ) => {
    const confirmed = await showDeleteConfirm(
      `${requiredDocument.moduleName || `Module #${requiredDocument.moduleId}`} - ${requiredDocument.documentTypeName || `Document Type #${requiredDocument.documentTypeId}`}`,
      "required document",
    );

    if (!confirmed) return;

    try {
      setRequiredDocumentLoading(true);
      await requiredDocumentService.deleteRequiredDocument(requiredDocument.id);
      showSuccessToast("Required document deleted successfully");
      await fetchRequiredDocuments();
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setRequiredDocumentLoading(false);
    }
  };

  const handleSaveSettings = async (section: string) => {
    setSaving(true);
    try {
      const settingsToUpdate: UpdateSettingDto[] = [];

      switch (section.toLowerCase()) {
        case "general":
          settingsToUpdate.push(
            {
              key: "AppName",
              value: generalSettings.appName,
              category: "general",
              description: "Application name",
            },
            {
              key: "AppDescription",
              value: generalSettings.appDescription,
              category: "general",
              description: "Application description",
            },
            {
              key: "Declaration",
              value: generalSettings.declaration,
              category: "general",
              description: "Declaration",
            },
            {
              key: "MinistryName",
              value: generalSettings.ministryName,
              category: "general",
              description: "Ministry name",
            },
            {
              key: "DefaultCurrency",
              value: generalSettings.currency,
              category: "general",
              description: "Default currency",
            },
            {
              key: "DateFormat",
              value: generalSettings.dateFormat,
              category: "general",
              description: "Date format",
            },

            {
              key: "MinimumAge",
              value: generalSettings.minimumAge,
              category: "general",
              description: "Minimum age for membership",
            },
          );
          break;
        case "email":
          settingsToUpdate.push(
            {
              key: "SmtpHost",
              value: emailSettings.smtpHost,
              category: "email",
              description: "SMTP server host",
            },
            {
              key: "SmtpPort",
              value: emailSettings.smtpPort,
              category: "email",
              description: "SMTP server port",
            },
            {
              key: "SmtpUser",
              value: emailSettings.smtpUser,
              category: "email",
              description: "SMTP username",
            },
            {
              key: "SmtpPassword",
              value: emailSettings.smtpPassword,
              category: "email",
              description: "SMTP password",
            },
            {
              key: "FromEmail",
              value: emailSettings.fromEmail,
              category: "email",
              description: "From email address",
            },
            {
              key: "FromName",
              value: emailSettings.fromName,
              category: "email",
              description: "From name",
            },
          );
          break;
        case "notification":
          settingsToUpdate.push(
            {
              key: "EmailNotifications",
              value: String(notificationSettings.emailNotifications),
              category: "notifications",
              description: "Enable email notifications",
            },
            {
              key: "SmsNotifications",
              value: String(notificationSettings.smsNotifications),
              category: "notifications",
              description: "Enable SMS notifications",
            },
            {
              key: "SystemAlerts",
              value: String(notificationSettings.systemAlerts),
              category: "notifications",
              description: "Enable system alerts",
            },
            {
              key: "WorkflowReminders",
              value: String(notificationSettings.workflowReminders),
              category: "notifications",
              description: "Enable workflow reminders",
            },
          );
          break;
        case "security":
          settingsToUpdate.push(
            {
              key: "SessionTimeout",
              value: securitySettings.sessionTimeout,
              category: "security",
              description: "Session timeout in minutes",
            },
            {
              key: "PasswordMinLength",
              value: securitySettings.passwordMinLength,
              category: "security",
              description: "Minimum password length",
            },
            {
              key: "RequireSpecialChar",
              value: String(securitySettings.requireSpecialChar),
              category: "security",
              description: "Require special character in password",
            },
            {
              key: "RequireNumber",
              value: String(securitySettings.requireNumber),
              category: "security",
              description: "Require number in password",
            },
            {
              key: "RequireUppercase",
              value: String(securitySettings.requireUppercase),
              category: "security",
              description: "Require uppercase in password",
            },
            {
              key: "EnableTwoFactor",
              value: String(securitySettings.enableTwoFactor),
              category: "security",
              description: "Enable two-factor authentication",
            },
          );
          break;
      }

      await settingsService.batchUpdateSettings(settingsToUpdate);

      // Refresh settings to get latest values
      await fetchSettings();

      showSuccessToast(`${section} settings saved successfully!`);
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={`dashboard-body ${sidebarOpen ? "sidebar-open" : "sidebar-closed"}`}
    >
      <Topbar
        onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
        sidebarOpen={sidebarOpen}
      />
      <Sidebar isOpen={sidebarOpen} />

      <div className="main-content">
        <div className="settings-page">
          <BusyOverlay
            isVisible={loading || saving}
            message={saving ? "Saving settings..." : "Loading settings..."}
          />

          {/* Accordion Controls */}
          <div className="settings-accordion-controls">
            <button type="button" className="btn-link" onClick={expandAll}>
              Expand All
            </button>
            <span className="divider">|</span>
            <button type="button" className="btn-link" onClick={collapseAll}>
              Collapse All
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="settings-content">
              <div className="alert alert-error">{error}</div>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="settings-content">
              <div
                style={{ textAlign: "center", padding: "var(--spacing-xl)" }}
              >
                <div className="spinner"></div>
                <p
                  style={{
                    marginTop: "var(--spacing-md)",
                    color: "var(--text-secondary)",
                  }}
                >
                  Loading settings...
                </p>
              </div>
            </div>
          )}

          {/* Content */}
          {!loading && (
            <div className="settings-content">
              <div className="accordion-container">
                {/* General Settings */}
                <div
                  className={`accordion-item ${expandedSections.has("general") ? "expanded" : ""}`}
                >
                  <button
                    className="accordion-header"
                    onClick={() => toggleSection("general")}
                    aria-expanded={expandedSections.has("general")}
                  >
                    <div className="accordion-title">
                      <span className="accordion-icon">⚙️</span>
                      General Settings
                    </div>
                    <span className="accordion-arrow">
                      {expandedSections.has("general") ? "▼" : "▶"}
                    </span>
                  </button>
                  {expandedSections.has("general") && (
                    <div className="accordion-content">
                      <div className="settings-form">
                        <div className="form-group">
                          <input
                            type="text"
                            className="form-input"
                            placeholder=" "
                            value={generalSettings.appName}
                            onChange={(e) =>
                              setGeneralSettings({
                                ...generalSettings,
                                appName: e.target.value,
                              })
                            }
                          />
                          <label className="form-label">Application Name</label>
                        </div>
                        <div className="form-group">
                          <textarea
                            className="form-input"
                            placeholder=" "
                            rows={3}
                            value={generalSettings.appDescription}
                            onChange={(e) =>
                              setGeneralSettings({
                                ...generalSettings,
                                appDescription: e.target.value,
                              })
                            }
                          />
                          <label className="form-label">
                            Application Description
                          </label>
                        </div>
                        <div className="form-group">
                          <textarea
                            className="form-input"
                            placeholder=" "
                            rows={4}
                            value={generalSettings.declaration}
                            onChange={(e) =>
                              setGeneralSettings({
                                ...generalSettings,
                                declaration: e.target.value,
                              })
                            }
                          />
                          <label className="form-label">Declaration</label>
                        </div>
                        <div className="form-group">
                          <input
                            type="text"
                            className="form-input"
                            placeholder=" "
                            value={generalSettings.ministryName}
                            onChange={(e) =>
                              setGeneralSettings({
                                ...generalSettings,
                                ministryName: e.target.value,
                              })
                            }
                          />
                          <label className="form-label">Ministry Name</label>
                        </div>
                        <div className="form-row">
                          <div className="form-group">
                            <select
                              className="form-input"
                              value={generalSettings.currency}
                              onChange={(e) =>
                                setGeneralSettings({
                                  ...generalSettings,
                                  currency: e.target.value,
                                })
                              }
                            >
                              <option value="">Select Default Currency</option>
                              {currencies.map((currency) => (
                                <option
                                  key={currency.id}
                                  value={currency.code || currency.name}
                                >
                                  {currency.name}
                                  {currency.code ? ` (${currency.code})` : ""}
                                </option>
                              ))}
                            </select>
                            <label className="form-label">
                              Default Currency
                            </label>
                          </div>
                        </div>
                        <div className="form-row">
                          <div className="form-group">
                            <input
                              type="number"
                              className="form-input"
                              placeholder=" "
                              value={generalSettings.minimumAge}
                              onChange={(e) =>
                                setGeneralSettings({
                                  ...generalSettings,
                                  minimumAge: e.target.value,
                                })
                              }
                              min="0"
                              max="100"
                            />
                            <label className="form-label">
                              Minimum User Age
                            </label>
                          </div>
                        </div>
                        <div className="form-actions">
                          <button
                            type="button"
                            className="btn btn--primary"
                            onClick={() => handleSaveSettings("General")}
                            disabled={saving}
                          >
                            {saving ? "Saving..." : "Save Changes"}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Email Settings */}
                <div
                  className={`accordion-item ${expandedSections.has("email") ? "expanded" : ""}`}
                >
                  <button
                    className="accordion-header"
                    onClick={() => toggleSection("email")}
                    aria-expanded={expandedSections.has("email")}
                  >
                    <div className="accordion-title">
                      <span className="accordion-icon">📧</span>
                      Email Settings
                    </div>
                    <span className="accordion-arrow">
                      {expandedSections.has("email") ? "▼" : "▶"}
                    </span>
                  </button>
                  {expandedSections.has("email") && (
                    <div className="accordion-content">
                      <div className="settings-form">
                        <div className="form-row">
                          <div className="form-group">
                            <input
                              type="text"
                              className="form-input"
                              placeholder=" "
                              value={emailSettings.smtpHost}
                              onChange={(e) =>
                                setEmailSettings({
                                  ...emailSettings,
                                  smtpHost: e.target.value,
                                })
                              }
                            />
                            <label className="form-label">SMTP Host</label>
                          </div>
                          <div className="form-group">
                            <input
                              type="text"
                              className="form-input"
                              placeholder=" "
                              value={emailSettings.smtpPort}
                              onChange={(e) =>
                                setEmailSettings({
                                  ...emailSettings,
                                  smtpPort: e.target.value,
                                })
                              }
                            />
                            <label className="form-label">SMTP Port</label>
                          </div>
                        </div>
                        <div className="form-row">
                          <div className="form-group">
                            <input
                              type="text"
                              className="form-input"
                              placeholder=" "
                              value={emailSettings.smtpUser}
                              onChange={(e) =>
                                setEmailSettings({
                                  ...emailSettings,
                                  smtpUser: e.target.value,
                                })
                              }
                            />
                            <label className="form-label">SMTP Username</label>
                          </div>
                          <div className="form-group">
                            <input
                              type="password"
                              className="form-input"
                              placeholder=" "
                              value={emailSettings.smtpPassword}
                              onChange={(e) =>
                                setEmailSettings({
                                  ...emailSettings,
                                  smtpPassword: e.target.value,
                                })
                              }
                            />
                            <label className="form-label">SMTP Password</label>
                          </div>
                        </div>
                        <div className="form-row">
                          <div className="form-group">
                            <input
                              type="email"
                              className="form-input"
                              placeholder=" "
                              value={emailSettings.fromEmail}
                              onChange={(e) =>
                                setEmailSettings({
                                  ...emailSettings,
                                  fromEmail: e.target.value,
                                })
                              }
                            />
                            <label className="form-label">From Email</label>
                          </div>
                          <div className="form-group">
                            <input
                              type="text"
                              className="form-input"
                              placeholder=" "
                              value={emailSettings.fromName}
                              onChange={(e) =>
                                setEmailSettings({
                                  ...emailSettings,
                                  fromName: e.target.value,
                                })
                              }
                            />
                            <label className="form-label">From Name</label>
                          </div>
                        </div>
                        <div className="form-actions">
                          <button
                            type="button"
                            className="btn btn--primary"
                            onClick={() => handleSaveSettings("Email")}
                            disabled={saving}
                          >
                            {saving ? "Saving..." : "Save Changes"}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Notification Settings */}
                <div
                  className={`accordion-item ${expandedSections.has("notifications") ? "expanded" : ""}`}
                >
                  <button
                    className="accordion-header"
                    onClick={() => toggleSection("notifications")}
                    aria-expanded={expandedSections.has("notifications")}
                  >
                    <div className="accordion-title">
                      <span className="accordion-icon">🔔</span>
                      Notification Settings
                    </div>
                    <span className="accordion-arrow">
                      {expandedSections.has("notifications") ? "▼" : "▶"}
                    </span>
                  </button>
                  {expandedSections.has("notifications") && (
                    <div className="accordion-content">
                      <div className="settings-form">
                        <div className="toggle-group">
                          <div className="toggle-item">
                            <div className="toggle-info">
                              <span className="toggle-label">
                                Email Notifications
                              </span>
                              <span className="toggle-description">
                                {notificationDescriptions.emailNotifications}
                              </span>
                            </div>
                            <label className="toggle-switch">
                              <input
                                type="checkbox"
                                checked={
                                  notificationSettings.emailNotifications
                                }
                                onChange={(e) =>
                                  setNotificationSettings({
                                    ...notificationSettings,
                                    emailNotifications: e.target.checked,
                                  })
                                }
                              />
                              <span className="toggle-slider"></span>
                            </label>
                          </div>
                          <div className="toggle-item">
                            <div className="toggle-info">
                              <span className="toggle-label">
                                SMS Notifications
                              </span>
                              <span className="toggle-description">
                                {notificationDescriptions.smsNotifications}
                              </span>
                            </div>
                            <label className="toggle-switch">
                              <input
                                type="checkbox"
                                checked={notificationSettings.smsNotifications}
                                onChange={(e) =>
                                  setNotificationSettings({
                                    ...notificationSettings,
                                    smsNotifications: e.target.checked,
                                  })
                                }
                              />
                              <span className="toggle-slider"></span>
                            </label>
                          </div>
                          <div className="toggle-item">
                            <div className="toggle-info">
                              <span className="toggle-label">
                                System Alerts
                              </span>
                              <span className="toggle-description">
                                {notificationDescriptions.systemAlerts}
                              </span>
                            </div>
                            <label className="toggle-switch">
                              <input
                                type="checkbox"
                                checked={notificationSettings.systemAlerts}
                                onChange={(e) =>
                                  setNotificationSettings({
                                    ...notificationSettings,
                                    systemAlerts: e.target.checked,
                                  })
                                }
                              />
                              <span className="toggle-slider"></span>
                            </label>
                          </div>

                          <div className="toggle-item">
                            <div className="toggle-info">
                              <span className="toggle-label">
                                Workflow Reminders
                              </span>
                              <span className="toggle-description">
                                {notificationDescriptions.workflowReminders}
                              </span>
                            </div>
                            <label className="toggle-switch">
                              <input
                                type="checkbox"
                                checked={notificationSettings.workflowReminders}
                                onChange={(e) =>
                                  setNotificationSettings({
                                    ...notificationSettings,
                                    workflowReminders: e.target.checked,
                                  })
                                }
                              />
                              <span className="toggle-slider"></span>
                            </label>
                          </div>
                        </div>
                        <div className="form-actions">
                          <button
                            type="button"
                            className="btn btn--primary"
                            onClick={() => handleSaveSettings("Notification")}
                            disabled={saving}
                          >
                            {saving ? "Saving..." : "Save Changes"}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Security Settings */}
                <div
                  className={`accordion-item ${expandedSections.has("security") ? "expanded" : ""}`}
                >
                  <button
                    className="accordion-header"
                    onClick={() => toggleSection("security")}
                    aria-expanded={expandedSections.has("security")}
                  >
                    <div className="accordion-title">
                      <span className="accordion-icon">🔒</span>
                      Security Settings
                    </div>
                    <span className="accordion-arrow">
                      {expandedSections.has("security") ? "▼" : "▶"}
                    </span>
                  </button>
                  {expandedSections.has("security") && (
                    <div className="accordion-content">
                      <div className="settings-form">
                        <div className="form-row">
                          <div className="form-group">
                            <input
                              type="number"
                              className="form-input"
                              placeholder=" "
                              value={securitySettings.sessionTimeout}
                              onChange={(e) =>
                                setSecuritySettings({
                                  ...securitySettings,
                                  sessionTimeout: e.target.value,
                                })
                              }
                              min="5"
                              max="480"
                            />
                            <label className="form-label">
                              Session Timeout (minutes)
                            </label>
                          </div>
                          <div className="form-group">
                            <input
                              type="number"
                              className="form-input"
                              placeholder=" "
                              value={securitySettings.passwordMinLength}
                              onChange={(e) =>
                                setSecuritySettings({
                                  ...securitySettings,
                                  passwordMinLength: e.target.value,
                                })
                              }
                              min="6"
                              max="32"
                            />
                            <label className="form-label">
                              Minimum Password Length
                            </label>
                          </div>
                        </div>
                        <div className="toggle-group">
                          <div className="toggle-item">
                            <div className="toggle-info">
                              <span className="toggle-label">
                                Require Special Character
                              </span>
                              <span className="toggle-description">
                                Passwords must contain at least one special
                                character
                              </span>
                            </div>
                            <label className="toggle-switch">
                              <input
                                type="checkbox"
                                checked={securitySettings.requireSpecialChar}
                                onChange={(e) =>
                                  setSecuritySettings({
                                    ...securitySettings,
                                    requireSpecialChar: e.target.checked,
                                  })
                                }
                              />
                              <span className="toggle-slider"></span>
                            </label>
                          </div>
                          <div className="toggle-item">
                            <div className="toggle-info">
                              <span className="toggle-label">
                                Require Number
                              </span>
                              <span className="toggle-description">
                                Passwords must contain at least one number
                              </span>
                            </div>
                            <label className="toggle-switch">
                              <input
                                type="checkbox"
                                checked={securitySettings.requireNumber}
                                onChange={(e) =>
                                  setSecuritySettings({
                                    ...securitySettings,
                                    requireNumber: e.target.checked,
                                  })
                                }
                              />
                              <span className="toggle-slider"></span>
                            </label>
                          </div>
                          <div className="toggle-item">
                            <div className="toggle-info">
                              <span className="toggle-label">
                                Require Uppercase
                              </span>
                              <span className="toggle-description">
                                Passwords must contain at least one uppercase
                                letter
                              </span>
                            </div>
                            <label className="toggle-switch">
                              <input
                                type="checkbox"
                                checked={securitySettings.requireUppercase}
                                onChange={(e) =>
                                  setSecuritySettings({
                                    ...securitySettings,
                                    requireUppercase: e.target.checked,
                                  })
                                }
                              />
                              <span className="toggle-slider"></span>
                            </label>
                          </div>
                          <div className="toggle-item">
                            <div className="toggle-info">
                              <span className="toggle-label">
                                Enable Two-Factor Authentication
                              </span>
                              <span className="toggle-description">
                                Require 2FA for all users
                              </span>
                            </div>
                            <label className="toggle-switch">
                              <input
                                type="checkbox"
                                checked={securitySettings.enableTwoFactor}
                                onChange={(e) =>
                                  setSecuritySettings({
                                    ...securitySettings,
                                    enableTwoFactor: e.target.checked,
                                  })
                                }
                              />
                              <span className="toggle-slider"></span>
                            </label>
                          </div>
                        </div>
                        <div className="form-actions">
                          <button
                            type="button"
                            className="btn btn--primary"
                            onClick={() => handleSaveSettings("Security")}
                            disabled={saving}
                          >
                            {saving ? "Saving..." : "Save Changes"}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Service Charges */}
                <div
                  className={`accordion-item ${expandedSections.has("serviceCharges") ? "expanded" : ""}`}
                >
                  <button
                    className="accordion-header"
                    onClick={() => toggleSection("serviceCharges")}
                    aria-expanded={expandedSections.has("serviceCharges")}
                  >
                    <div className="accordion-title">
                      <span className="accordion-icon">💰</span>
                      Service Charges
                    </div>
                    <span className="accordion-arrow">
                      {expandedSections.has("serviceCharges") ? "▼" : "▶"}
                    </span>
                  </button>
                  {expandedSections.has("serviceCharges") && (
                    <div className="accordion-content">
                      <div className="settings-form">
                        <div className="service-price-header">
                          <button
                            type="button"
                            className="btn btn--primary"
                            onClick={() => openServiceChargeModal()}
                            disabled={serviceChargeLoading}
                          >
                            + Add Service Charge
                          </button>
                        </div>

                        {serviceCharges.length === 0 ? (
                          <div
                            style={{
                              textAlign: "center",
                              padding: "2rem",
                              color: "#666",
                            }}
                          >
                            No service prices configured yet.
                          </div>
                        ) : (
                          <div className="table-container">
                            <table
                              className="data-table"
                              style={{ width: "100%" }}
                            >
                              <thead>
                                <tr>
                                  <th>Service Category</th>
                                  <th>LRA Payment</th>
                                  <th>Operational Cost</th>
                                  <th>Currency</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {serviceCharges.map((serviceCharge) => (
                                  <tr key={serviceCharge.id}>
                                    <td>
                                      {serviceCharge.serviceCategoryName ||
                                        `Category ID: ${serviceCharge.serviceCategoryId}`}
                                    </td>
                                    <td>
                                      {serviceCharge.lraPayment.toFixed(2)}
                                    </td>
                                    <td>
                                      {serviceCharge.operationalCost.toFixed(2)}
                                    </td>
                                    <td>
                                      {serviceCharge.currencyName ||
                                        `Currency ID: ${serviceCharge.currencyId}`}
                                    </td>
                                    <td>
                                      <button
                                        className="btn btn--sm btn--outline"
                                        onClick={() =>
                                          openServiceChargeModal(serviceCharge)
                                        }
                                        disabled={serviceChargeLoading}
                                      >
                                        Edit
                                      </button>
                                      <button
                                        className="btn btn--sm btn--danger"
                                        onClick={() =>
                                          handleDeleteServiceCharge(
                                            serviceCharge,
                                          )
                                        }
                                        disabled={serviceChargeLoading}
                                        style={{ marginLeft: "0.5rem" }}
                                      >
                                        Delete
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Required Documents */}
                <div
                  className={`accordion-item ${expandedSections.has("requiredDocuments") ? "expanded" : ""}`}
                >
                  <button
                    className="accordion-header"
                    onClick={() => toggleSection("requiredDocuments")}
                    aria-expanded={expandedSections.has("requiredDocuments")}
                  >
                    <div className="accordion-title">
                      <span className="accordion-icon">📎</span>
                      Required Documents
                    </div>
                    <span className="accordion-arrow">
                      {expandedSections.has("requiredDocuments") ? "▼" : "▶"}
                    </span>
                  </button>
                  {expandedSections.has("requiredDocuments") && (
                    <div className="accordion-content">
                      <div className="settings-form">
                        <div className="service-price-header">
                          <button
                            type="button"
                            className="btn btn--primary"
                            onClick={() => openRequiredDocumentModal()}
                            disabled={requiredDocumentLoading}
                          >
                            + Add Required Document
                          </button>
                        </div>

                        {requiredDocuments.length === 0 ? (
                          <div
                            style={{
                              textAlign: "center",
                              padding: "2rem",
                              color: "#666",
                            }}
                          >
                            No required documents configured yet.
                          </div>
                        ) : (
                          <div className="table-container">
                            <table
                              className="data-table"
                              style={{ width: "100%" }}
                            >
                              <thead>
                                <tr>
                                  <th>Module</th>
                                  <th>Document Type</th>
                                  <th>Required</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {requiredDocuments.map((requiredDocument) => (
                                  <tr key={requiredDocument.id}>
                                    <td>
                                      {requiredDocument.moduleName ||
                                        `Module ID: ${requiredDocument.moduleId}`}
                                    </td>
                                    <td>
                                      {requiredDocument.documentTypeName ||
                                        `Document Type ID: ${requiredDocument.documentTypeId}`}
                                    </td>
                                    <td>
                                      <span
                                        className={`status-badge ${requiredDocument.isRequired ? "status-active" : "status-inactive"}`}
                                      >
                                        {requiredDocument.isRequired
                                          ? "Yes"
                                          : "No"}
                                      </span>
                                    </td>
                                    <td>
                                      <button
                                        className="btn btn--sm btn--outline"
                                        onClick={() =>
                                          openRequiredDocumentModal(
                                            requiredDocument,
                                          )
                                        }
                                        disabled={requiredDocumentLoading}
                                      >
                                        Edit
                                      </button>
                                      <button
                                        className="btn btn--sm btn--danger"
                                        onClick={() =>
                                          handleDeleteRequiredDocument(
                                            requiredDocument,
                                          )
                                        }
                                        disabled={requiredDocumentLoading}
                                        style={{ marginLeft: "0.5rem" }}
                                      >
                                        Delete
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Service Price Modal */}
          {showServiceChargeModal && (
            <div className="modal-overlay modal-overlay--side-panel">
              <div
                className="modal-content modal-content--side-panel"
                style={{ width: "20%" }}
              >
                <div className="modal-header">
                  <h2 className="modal-title">
                    {editingServiceCharge
                      ? "Edit Service Charge"
                      : "Add Service Charge"}
                  </h2>
                  <button
                    type="button"
                    className="modal-close-btn"
                    onClick={closeServiceChargeModal}
                    disabled={serviceChargeLoading}
                  >
                    ✕
                  </button>
                </div>

                <form
                  className="modal-form"
                  onSubmit={handleServiceChargeSubmit}
                >
                  {serviceChargeError && (
                    <div className="alert alert-error">
                      <span className="alert-icon">⚠️</span>
                      <span>{serviceChargeError}</span>
                      <button
                        type="button"
                        onClick={() => setServiceChargeError("")}
                        className="alert-close"
                      >
                        ✕
                      </button>
                    </div>
                  )}

                  <div
                    className="form-grid"
                    style={{ gridTemplateColumns: "1fr" }}
                  >
                    <div className="form-group has-value">
                      <select
                        className="form-input"
                        value={serviceChargeFormData.serviceCategoryId}
                        onChange={(e) =>
                          setServiceChargeFormData({
                            ...serviceChargeFormData,
                            serviceCategoryId: parseInt(e.target.value, 10),
                          })
                        }
                        required
                      >
                        <option value={0}>Select Service Category</option>
                        {serviceCategories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                      <label className="form-label">
                        Service Category <span className="required">*</span>
                      </label>
                    </div>

                    <div className="form-group has-value">
                      <select
                        className="form-input"
                        value={serviceChargeFormData.currencyId}
                        onChange={(e) =>
                          setServiceChargeFormData({
                            ...serviceChargeFormData,
                            currencyId: parseInt(e.target.value, 10),
                          })
                        }
                        required
                      >
                        <option value={0}>Select Currency</option>
                        {currencies.map((currency) => (
                          <option key={currency.id} value={currency.id}>
                            {currency.name}
                            {currency.code ? ` (${currency.code})` : ""}
                          </option>
                        ))}
                      </select>
                      <label className="form-label">
                        Currency <span className="required">*</span>
                      </label>
                    </div>

                    <div
                      className={`form-group ${serviceChargeFormData.lraPayment ? "has-value" : ""}`}
                    >
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="form-input"
                        value={serviceChargeFormData.lraPayment || ""}
                        onChange={(e) =>
                          setServiceChargeFormData({
                            ...serviceChargeFormData,
                            lraPayment: parseFloat(e.target.value) || 0,
                          })
                        }
                        placeholder=" "
                        required
                      />
                      <label className="form-label">
                        LRA Payment <span className="required">*</span>
                      </label>
                    </div>

                    <div
                      className={`form-group ${serviceChargeFormData.operationalCost ? "has-value" : ""}`}
                    >
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="form-input"
                        value={serviceChargeFormData.operationalCost || ""}
                        onChange={(e) =>
                          setServiceChargeFormData({
                            ...serviceChargeFormData,
                            operationalCost: parseFloat(e.target.value) || 0,
                          })
                        }
                        placeholder=" "
                        required
                      />
                      <label className="form-label">
                        Operational Cost <span className="required">*</span>
                      </label>
                    </div>
                  </div>

                  <div className="modal-actions">
                    <button
                      type="button"
                      className="btn btn--outline"
                      onClick={closeServiceChargeModal}
                      disabled={serviceChargeLoading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn--primary"
                      disabled={serviceChargeLoading}
                    >
                      {serviceChargeLoading ? (
                        <>
                          <span className="spinner spinner--sm"></span>
                          {editingServiceCharge ? "Updating..." : "Creating..."}
                        </>
                      ) : (
                        <>{editingServiceCharge ? "Update" : "Create"}</>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Required Document Modal */}
          {showRequiredDocumentModal && (
            <div className="modal-overlay modal-overlay--side-panel">
              <div
                className="modal-content modal-content--side-panel"
                style={{ width: "20%" }}
              >
                <div className="modal-header">
                  <h2 className="modal-title">
                    {editingRequiredDocument
                      ? "Edit Required Document"
                      : "Add Required Document"}
                  </h2>
                  <button
                    type="button"
                    className="modal-close-btn"
                    onClick={closeRequiredDocumentModal}
                    disabled={requiredDocumentLoading}
                  >
                    ✕
                  </button>
                </div>

                <form
                  className="modal-form"
                  onSubmit={handleRequiredDocumentSubmit}
                >
                  {requiredDocumentError && (
                    <div className="alert alert-error">
                      <span className="alert-icon">⚠️</span>
                      <span>{requiredDocumentError}</span>
                      <button
                        type="button"
                        onClick={() => setRequiredDocumentError("")}
                        className="alert-close"
                      >
                        ✕
                      </button>
                    </div>
                  )}

                  <div
                    className="form-grid"
                    style={{ gridTemplateColumns: "1fr" }}
                  >
                    <div className="form-group has-value">
                      <select
                        className="form-input"
                        value={requiredDocumentFormData.moduleId}
                        onChange={(e) =>
                          setRequiredDocumentFormData({
                            ...requiredDocumentFormData,
                            moduleId: parseInt(e.target.value, 10),
                          })
                        }
                        required
                      >
                        <option value={0}>Select Module</option>
                        {modules.map((module) => (
                          <option key={module.id} value={module.id}>
                            {module.name}
                          </option>
                        ))}
                      </select>
                      <label className="form-label">
                        Module <span className="required">*</span>
                      </label>
                    </div>

                    <div className="form-group has-value">
                      <select
                        className="form-input"
                        value={requiredDocumentFormData.documentTypeId}
                        onChange={(e) =>
                          setRequiredDocumentFormData({
                            ...requiredDocumentFormData,
                            documentTypeId: parseInt(e.target.value, 10),
                          })
                        }
                        required
                      >
                        <option value={0}>Select Document Type</option>
                        {documentTypes.map((documentType) => (
                          <option key={documentType.id} value={documentType.id}>
                            {documentType.name}
                          </option>
                        ))}
                      </select>
                      <label className="form-label">
                        Document Type <span className="required">*</span>
                      </label>
                    </div>

                    <div className="toggle-group">
                      <div className="toggle-item" style={{ marginBottom: 0 }}>
                        <div className="toggle-info">
                          <span className="toggle-label">Is Required</span>
                        </div>
                        <label className="toggle-switch">
                          <input
                            type="checkbox"
                            checked={requiredDocumentFormData.isRequired}
                            onChange={(e) =>
                              setRequiredDocumentFormData({
                                ...requiredDocumentFormData,
                                isRequired: e.target.checked,
                              })
                            }
                          />
                          <span className="toggle-slider"></span>
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="modal-actions">
                    <button
                      type="button"
                      className="btn btn--outline"
                      onClick={closeRequiredDocumentModal}
                      disabled={requiredDocumentLoading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn--primary"
                      disabled={requiredDocumentLoading}
                    >
                      {requiredDocumentLoading ? (
                        <>
                          <span className="spinner spinner--sm"></span>
                          {editingRequiredDocument
                            ? "Updating..."
                            : "Creating..."}
                        </>
                      ) : (
                        <>{editingRequiredDocument ? "Update" : "Create"}</>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
        <Footer />
      </div>
    </div>
  );
};
